import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, map, forkJoin, catchError, finalize, BehaviorSubject } from 'rxjs';
import {
    EnterpriseCropYearLocation,
    MapYearLocation,
    RoleCropYearLocation,
    RoleYearLocation,
} from '../interfaces/cropmap-access.interface';
import {
    AccessEnterpriseProxy,
    AccessEnterpriseViewModel,
    AccessRoleProxy,
    AccessRoleViewModel,
    CountryAddressHierarchyViewModel,
    CountryHierarchyViewModel,
    CropMapCropViewModel,
    CropMapLayerViewModel,
    CropMapLocationCityResponse,
    CropMapLocationCountryResponse,
    CropMapLocationDistrictResponse,
    CropMapLocationRegionResponse,
    CropMapLocationStateResponse,
    CropMapSeasonViewModel,
    LookupProxy,
} from '../../proxy/proxies';
import { CityModel, DistrictModel, RegionModel, StateModel } from '../models/config.models';
import { LocalStorageService } from './storage/local-storage.service';
import { GlobalLoaderService } from './global-loader.service';

export type GetEnterpriseAndRoleAccessOptions = {
    /** When false, skip full-screen GlobalLoaderService (e.g. Change Enterprise button loading). Default true. */
    useGlobalLoader?: boolean;
};

@Injectable({ providedIn: 'root' })
export class CropMapAccessService {
    private readonly accessEnterpriseProxy = inject(AccessEnterpriseProxy);
    private readonly accessRoleProxy = inject(AccessRoleProxy);
    private readonly lookupProxy = inject(LookupProxy);
    private readonly localStorage = inject(LocalStorageService);
    private readonly globalLoader = inject(GlobalLoaderService);

    // ─── Enterprise access (simple load by ID) ───────────────────────────
    private static readonly STORAGE_KEY = 'enterpriseAccess';

    private readonly accessSubject = new BehaviorSubject<AccessEnterpriseViewModel | null>(
        this.loadFromStorage(),
    );

    readonly access$ = this.accessSubject.asObservable();

    // ─── Enterprise locations ────────────────────────────────────────────
    enterpriseCropYearLocations: EnterpriseCropYearLocation[] = [];

    // ─── Authorized (role-filtered) locations ────────────────────────────
    authorizedCropYearLocations: RoleCropYearLocation[] = [];

    // ─── Mapbox layers ───────────────────────────────────────────────────
    mapboxLayerConfigs: CropMapLayerViewModel[] = [];

    /** True when the user is signed in but has no enterprise with CropMap access (demo shell + overlay). */
    readonly noCropMapAccess = signal(false);

    /** Enables field observation UI on plantation after access load succeeds; cleared on {@link resetAll}. */
    readonly fieldObservationEnterpriseEnabled = signal(true);

    /** Matches last successful {@link getEnterpriseAndRoleAccess} for user + enterprise + role. */
    private loadedAccessKey: string | null = null;

    /** Incremented on reset/clear and at fetch start; stale HTTP responses are ignored. */
    private accessLoadGeneration = 0;

    // ═════════════════════════════════════════════════════════════════════
    // Main entry point
    // ═════════════════════════════════════════════════════════════════════

    private buildLoadedAccessKey(userId: string, enterpriseId: string, roleId: string): string {
        return `${userId}|${enterpriseId}|${roleId}`;
    }

    private invalidateAccessLoadCache(): void {
        this.loadedAccessKey = null;
        this.accessLoadGeneration++;
    }

    getEnterpriseAndRoleAccess(
        enterpriseId: string,
        roleId?: string,
        options?: GetEnterpriseAndRoleAccessOptions,
    ): Observable<boolean> {
        if (!enterpriseId || !roleId) return of(false);

        const userId = this.localStorage.getItem<string>('userId');
        if (!userId) return of(false);

        // Cache is scoped to signed-in user + enterprise + role. Without userId in the key,
        // logging out and signing in as another user could reuse the previous user's years/locations.
        const requestedKey = this.buildLoadedAccessKey(userId, enterpriseId, roleId);
        if (
            this.loadedAccessKey === requestedKey &&
            this.authorizedCropYearLocations.length > 0
        ) {
            return of(true);
        }

        // New fetch: bump generation so in-flight responses from an older load are dropped, and
        // clear arrays so we never merge stale crop-year rows while HTTP is pending.
        const loadGeneration = ++this.accessLoadGeneration;
        this.enterpriseCropYearLocations = [];
        this.authorizedCropYearLocations = [];

        const useGlobalLoader = options?.useGlobalLoader !== false;

        // Enterprise rows = org crop-year coverage; role rows = role-scoped hierarchy (see compareEnterpriseAndAuthorizedLocations).
        if (useGlobalLoader) {
            this.globalLoader.show();
        }
        return forkJoin([
            this.accessEnterpriseProxy.accessEnterprise_GetByEnterpriseId(enterpriseId),
            this.accessRoleProxy.accessRole_GetByEnterpriseId(enterpriseId, roleId),
        ]).pipe(
            map(([enterpriseAccessRes, roleResponse]) => {
                if (loadGeneration !== this.accessLoadGeneration) {
                    return false;
                }

                const enterpriseAccessList = enterpriseAccessRes?.result ?? [];
                const accessRoles = roleResponse?.result ?? [];
                if (!enterpriseAccessList.length || !accessRoles.length) return false;

                for (const enterpriseAccess of enterpriseAccessList) {
                    this.addEnterpriseCropYearLocation(enterpriseAccess, this.enterpriseCropYearLocations);
                }

                for (const role of accessRoles) {
                    this.addRoleCropYearLocation(role, this.authorizedCropYearLocations);
                }

                this.fieldObservationEnterpriseEnabled.set(true);
                this.loadedAccessKey = requestedKey;
                this.compareEnterpriseAndAuthorizedLocations();
                return true;
            }),
            catchError((error) => {
                console.error('CropMapAccessService: getEnterpriseAndRole error', error);
                return of(false);
            }),
            finalize(() => {
                if (useGlobalLoader) {
                    this.globalLoader.hide();
                }
            }),
        );
    }

    private addEnterpriseCropYearLocation(
        access: AccessEnterpriseViewModel,
        target: EnterpriseCropYearLocation[],
    ): void {
        if (!access.cropId || !access.seasonId) return;

        const cropIdx = target.findIndex((row) => row.cropId == access.cropId);
        if (cropIdx == -1) {
            const cropYearLocation: EnterpriseCropYearLocation = {
                cropId: access.cropId,
                cropName: access.cropName as string,
                cropColor: access.cropColor as string,
                seasonId: access.seasonId,
                seasonName: access.season as string,
                yearLocations: [
                    {
                        year: access.year,
                        countries: [{ id: access.countryId, name: access.countryName as string }],
                    },
                ],
            };

            target.push(cropYearLocation);
        } else {
            const yearIdx = target[cropIdx].yearLocations.findIndex((yl) => yl.year == access.year);
            const countrySummary = { id: access.countryId, name: access.countryName as string };
            if (yearIdx == -1) {
                target[cropIdx].yearLocations.push({ year: access.year, countries: [countrySummary] });
            } else {
                const countries = target[cropIdx].yearLocations[yearIdx].countries;
                if (!countries.some((c) => c.id == countrySummary.id)) {
                    countries.push(countrySummary);
                }
            }
        }
    }

    private addRoleCropYearLocation(
        access: AccessRoleViewModel,
        target: RoleCropYearLocation[],
    ): void {
        if (!access.cropId || !access.seasonId) return;

        const incomingHierarchy = access.countriesHierarchy ?? [];
        const cropIdx = target.findIndex((row) => row.cropId == access.cropId);
        if (cropIdx == -1) {
            target.push({
                cropId: access.cropId,
                cropName: access.cropName ?? '',
                cropColor: access.cropColor ?? '',
                seasonId: access.seasonId,
                seasonName: access.seasonName ?? '',
                yearLocations: [{ year: access.year, locations: structuredClone(incomingHierarchy) }],
            });
            return;
        }

        const yearIdx = target[cropIdx].yearLocations.findIndex((yl) => yl.year == access.year);
        if (yearIdx == -1) {
            target[cropIdx].yearLocations.push({
                year: access.year,
                locations: structuredClone(incomingHierarchy),
            });
        } else {
            target[cropIdx].yearLocations[yearIdx].locations = this.mergeCountryHierarchies(
                target[cropIdx].yearLocations[yearIdx].locations,
                incomingHierarchy,
            );
        }
    }

    private mergeCountryHierarchies(
        baseHierarchy: CountryHierarchyViewModel[],
        incomingHierarchy: CountryHierarchyViewModel[],
    ): CountryHierarchyViewModel[] {
        const merged = structuredClone(baseHierarchy);
        for (const incomingCountry of incomingHierarchy) {
            const countryIdx = merged.findIndex((c) => c.countryId == incomingCountry.countryId);
            if (countryIdx === -1) {
                merged.push(structuredClone(incomingCountry));
                continue;
            }
            const existingCountry = merged[countryIdx];
            for (const region of incomingCountry.regions ?? []) {
                if (!(existingCountry.regions ?? []).some((r) => r.regionId == region.regionId)) {
                    (existingCountry.regions = existingCountry.regions ?? []).push(structuredClone(region));
                }
            }
            for (const state of incomingCountry.states ?? []) {
                if (!(existingCountry.states ?? []).some((s) => s.stateId == state.stateId)) {
                    (existingCountry.states = existingCountry.states ?? []).push(structuredClone(state));
                }
            }
        }
        return merged;
    }

    /**
     * API hierarchy → map country responses (regions/states with cities that have districts).
     */
    private hierarchyToCropMapCountries(
        hierarchy: CountryHierarchyViewModel[] | undefined,
    ): CropMapLocationCountryResponse[] {
        if (!hierarchy?.length) return [];

        const countries: CropMapLocationCountryResponse[] = [];

        for (const hierarchyCountry of hierarchy) {
            const country = new CropMapLocationCountryResponse();
            (country as any).countryId = hierarchyCountry.countryId;
            country.name = hierarchyCountry.name;

            country.regions = (hierarchyCountry.regions ?? [])
                .map((r) => ({
                    regionId: r.regionId,
                    name: r.name,
                    cities: (r.cities ?? [])
                        .filter((ci) => ci.districts?.length)
                        .map((ci) => ({
                            cityId: ci.cityId,
                            name: ci.name,
                            districts: ci.districts!.map((d) => ({
                                districtId: d.districtId,
                                name: d.name,
                            })),
                        })),
                }))
                .filter((r) => r.cities.length > 0) as any;

            country.states = (hierarchyCountry.states ?? [])
                .map((s) => ({
                    stateId: s.stateId,
                    name: s.name,
                    cities: (s.cities ?? [])
                        .filter((ci) => ci.districts?.length)
                        .map((ci) => ({
                            cityId: ci.cityId,
                            name: ci.name,
                            districts: ci.districts!.map((d) => ({
                                districtId: d.districtId,
                                name: d.name,
                            })),
                        })),
                }))
                .filter((s) => s.cities.length > 0) as any;

            if ((country.regions?.length ?? 0) > 0 || (country.states?.length ?? 0) > 0) {
                countries.push(country);
            }
        }

        return countries;
    }

    /** Enterprise API stores id+name only; expand to map {@link CropMapLocationCountryResponse} shells. */
    private enterpriseRefsToFlatCountries(
        countries: { id: string; name: string }[],
    ): CropMapLocationCountryResponse[] {
        return countries.map((country) => {
            const mapCountry = new CropMapLocationCountryResponse();
            (mapCountry as any).countryId = country.id;
            mapCountry.name = country.name;
            return mapCountry;
        });
    }

    private mergeCountriesIntoBucket(
        bucket: CropMapLocationCountryResponse[],
        incoming: CropMapLocationCountryResponse[],
    ): void {
        for (const country of incoming) {
            const locationIndex = bucket.findIndex((e) => e.countryId == country.countryId);
            if (locationIndex == -1) {
                bucket.push(country);
                continue;
            }

            const existing = bucket[locationIndex];
            const regions = country.regions ?? [];
            const states = country.states ?? [];

            if (regions.length > 0) {
                const eRegions = existing.regions ?? [];
                existing.regions = eRegions;
                for (const region of regions) {
                    const regionIndex = eRegions.findIndex((existing) => existing.regionId == region.regionId);
                    if (regionIndex == -1) {
                        eRegions.push(region);
                    } else {
                        const eCities = eRegions[regionIndex].cities ?? [];
                        eRegions[regionIndex].cities = eCities;
                        for (const city of region.cities ?? []) {
                            const cityIndex = eCities.findIndex((existing) => existing.cityId == city.cityId);
                            if (cityIndex == -1) {
                                eCities.push(city);
                            } else {
                                const eDistricts = eCities[cityIndex].districts ?? [];
                                eCities[cityIndex].districts = eDistricts;
                                for (const district of city.districts ?? []) {
                                    if (!eDistricts.some((e) => e.districtId == district.districtId)) {
                                        eDistricts.push(district);
                                    }
                                }
                            }
                        }
                    }
                }
            } else if (states.length > 0) {
                const eStates = existing.states ?? [];
                existing.states = eStates;
                for (const state of states) {
                    const stateIndex = eStates.findIndex((existing) => existing.stateId == state.stateId);
                    if (stateIndex == -1) {
                        eStates.push(state);
                    } else {
                        const eCities = eStates[stateIndex].cities ?? [];
                        eStates[stateIndex].cities = eCities;
                        for (const city of state.cities ?? []) {
                            const cityIndex = eCities.findIndex((existing) => existing.cityId == city.cityId);
                            if (cityIndex == -1) {
                                eCities.push(city);
                            } else {
                                const eDistricts = eCities[cityIndex].districts ?? [];
                                eCities[cityIndex].districts = eDistricts;
                                for (const district of city.districts ?? []) {
                                    if (!eDistricts.some((e) => e.districtId == district.districtId)) {
                                        eDistricts.push(district);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    // ═════════════════════════════════════════════════════════════════════
    // Query helpers – seasons, crops, years, countries, cities
    // ═════════════════════════════════════════════════════════════════════

    /** Compare API string ids with proxy view-model numeric ids from forms. */
    private idEq(leftId: unknown, rightId: unknown): boolean {
        return String(leftId) === String(rightId);
    }

    getAuthorizedYearLocations(): MapYearLocation[] {
        const years: MapYearLocation[] = [];
        for (const cropYearLocation of this.authorizedCropYearLocations) {
            for (const yearLocation of cropYearLocation.yearLocations) {
                const mapCountries = this.hierarchyToCropMapCountries(yearLocation.locations);
                const existingYear = years.find((y) => y.year === yearLocation.year);
                if (!existingYear) {
                    years.push({ year: yearLocation.year, locations: mapCountries });
                } else {
                    this.mergeCountriesIntoBucket(existingYear.locations, mapCountries);
                }
            }
        }
        return years.sort((yearA, yearB) => yearB.year - yearA.year);
    }

    getSeasons(type: 'enterprise' | 'authorized' = 'authorized'): CropMapSeasonViewModel[] {
        const seasons: CropMapSeasonViewModel[] = [];
        const cropYearRows =
            type == 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        for (const cropYearRow of cropYearRows) {
            if (!seasons.some((season) => this.idEq(season.id, cropYearRow.seasonId))) {
                seasons.push(
                    CropMapSeasonViewModel.fromJS({
                        id: cropYearRow.seasonId,
                        name: cropYearRow.seasonName,
                    }),
                );
            }
        }
        return seasons.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
    }

    getCropsBySeasonId(seasonIds: any[], type: 'enterprise' | 'authorized' = 'authorized'): CropMapCropViewModel[] {
        return this.collectCropsByFilter(seasonIds, {}, type);
    }

    getCropsByYear(
        seasonIds: any[],
        year: number,
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CropMapCropViewModel[] {
        return this.collectCropsByFilter(seasonIds, { year }, type);
    }

    getCropsByYearCountry(
        seasonIds: any[],
        year: number,
        countryId: any,
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CropMapCropViewModel[] {
        return this.collectCropsByFilter(seasonIds, { year, countryId }, type);
    }

    getCropsBySeasonCountry(
        seasonIds: any[],
        countryId: any,
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CropMapCropViewModel[] {
        return this.collectCropsByFilter(seasonIds, { countryId }, type);
    }

    private collectCropsByFilter(
        seasonIds: any[],
        filter: { year?: number; countryId?: unknown },
        type: 'enterprise' | 'authorized',
    ): CropMapCropViewModel[] {
        const rows =
            type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const { year, countryId } = filter;
        const needsYearLoc = year !== undefined || countryId !== undefined;
        const crops: CropMapCropViewModel[] = [];
        const seen = new Set<string>();
        for (const row of rows) {
            if (!seasonIds.some((seasonId) => this.idEq(seasonId, row.seasonId))) {
                continue;
            }
            if (seen.has(String(row.cropId))) {
                continue;
            }
            if (needsYearLoc) {
                const ok = row.yearLocations.some((yearLoc) => {
                    if (year !== undefined && yearLoc.year != year) {
                        return false;
                    }
                    if (countryId === undefined) {
                        return true;
                    }
                    if (type === 'enterprise') {
                        return (yearLoc as EnterpriseCropYearLocation['yearLocations'][number]).countries?.some(
                            (country) => country.id == countryId,
                        );
                    }
                    return (yearLoc as RoleYearLocation).locations?.some(
                        (hierarchy) => hierarchy.countryId == countryId,
                    );
                });
                if (!ok) {
                    continue;
                }
            }
            seen.add(String(row.cropId));
            crops.push(
                CropMapCropViewModel.fromJS({
                    id: row.cropId,
                    name: row.cropName,
                    color: row.cropColor,
                    seasonId: row.seasonId,
                }),
            );
        }
        return crops;
    }

    /**
     * Year/location buckets merged from crop-year rows, filtered by crop id(s) and optionally by season.
     * @param seasonIds When set (non-empty), only rows whose seasonId is included (authorized or enterprise crop-year rows).
     *        When `undefined`, any season matches (backward compatible). Empty array yields no rows.
     */
    getYearLocationByCropSeason(
        cropIds: any[],
        type: 'enterprise' | 'authorized' = 'authorized',
        seasonId?: any,
        seasonIds?: any[],
    ): MapYearLocation[] {
        const years: MapYearLocation[] = [];

        const matchesCrop = (cropId: string) => cropIds.some((cid) => this.idEq(cid, cropId));
        const matchesSeason = (sid: string) => {
            if (seasonIds !== undefined) {
                return seasonIds.length > 0 && seasonIds.some((s) => this.idEq(s, sid));
            }
            return seasonId == null || this.idEq(sid, seasonId);
        };

        if (type === 'enterprise') {
            const matchingRows = this.enterpriseCropYearLocations.filter(
                (row) => matchesCrop(row.cropId) && matchesSeason(row.seasonId),
            );
            for (const cropYearRow of matchingRows) {
                for (const yearLocation of cropYearRow.yearLocations) {
                    const mapCountries = this.enterpriseRefsToFlatCountries(yearLocation.countries ?? []);
                    let yearBucket = years.find((y) => y.year === yearLocation.year);
                    if (!yearBucket) {
                        yearBucket = { year: yearLocation.year, locations: [] };
                        years.push(yearBucket);
                    }
                    this.mergeCountriesIntoBucket(yearBucket.locations, mapCountries);
                }
            }
        } else {
            const matchingRows = this.authorizedCropYearLocations.filter(
                (row) => matchesCrop(row.cropId) && matchesSeason(row.seasonId),
            );
            for (const cropYearRow of matchingRows) {
                for (const yearLocation of cropYearRow.yearLocations) {
                    const mapCountries = this.hierarchyToCropMapCountries(yearLocation.locations);
                    let yearBucket = years.find((y) => y.year === yearLocation.year);
                    if (!yearBucket) {
                        yearBucket = { year: yearLocation.year, locations: [] };
                        years.push(yearBucket);
                    }
                    this.mergeCountriesIntoBucket(yearBucket.locations, mapCountries);
                }
            }
        }

        return years.sort((yearA, yearB) => yearB.year - yearA.year);
    }

    getCountriesByYear(yearLocations: MapYearLocation[]): CropMapLocationCountryResponse[] {
        const countries: CropMapLocationCountryResponse[] = [];
        for (const yearBucket of yearLocations) {
            for (const country of yearBucket.locations) {
                const idx = countries.findIndex((existing) => existing.countryId == country.countryId);
                if (idx === -1) {
                    countries.push(structuredClone(country));
                } else {
                    this.mergeCountriesIntoBucket(countries, [country]);
                }
            }
        }
        return countries;
    }

    getCitiesByCountry(country: CropMapLocationCountryResponse): CropMapLocationCityResponse[] {
        const cities: CropMapLocationCityResponse[] = [];
        for (const region of country.regions ?? []) {
            for (const city of region.cities ?? []) {
                if (!cities.find((existing) => existing.cityId == city.cityId)) cities.push(city);
            }
        }
        for (const state of country.states ?? []) {
            for (const city of state.cities ?? []) {
                if (!cities.find((existing) => existing.cityId == city.cityId)) cities.push(city);
            }
        }
        return cities;
    }

    /** Cities for a country across all authorized (role) data (same shape as year-scoped helpers). */

    getYearLocationsForSeasonAndYear(
        seasonId: any,
        year: number,
        cropIds?: any[],
        type: 'enterprise' | 'authorized' = 'authorized',
    ): MapYearLocation | null {
        const src = type === 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        const filtered = src.filter(
            (e) =>
                e.seasonId == seasonId &&
                (!cropIds?.length || cropIds.some((cid) => cid == e.cropId)),
        );
        if (!filtered.length) return null;

        const merged: MapYearLocation = { year, locations: [] };
        for (const item of filtered) {
            const yl = item.yearLocations.find((y) => y.year == year);
            if (!yl) continue;
            const slice =
                type === 'enterprise'
                    ? this.enterpriseRefsToFlatCountries(
                          (yl as EnterpriseCropYearLocation['yearLocations'][number]).countries ?? [],
                      )
                    : this.hierarchyToCropMapCountries((yl as RoleYearLocation).locations);
            this.mergeCountriesIntoBucket(merged.locations, slice);
        }

        for (const country of merged.locations) {
            const filterCities = (cities: any[] | undefined) =>
                (cities ?? []).filter((c: any) => c.districts?.length > 0);

            if (country.regions) {
                for (const region of country.regions) {
                    region.cities = filterCities(region.cities) as any;
                }
                country.regions = country.regions.filter((r) => (r.cities?.length ?? 0) > 0);
            }
            if (country.states) {
                for (const state of country.states) {
                    state.cities = filterCities(state.cities) as any;
                }
                country.states = country.states.filter((s) => (s.cities?.length ?? 0) > 0);
            }
        }
        merged.locations = merged.locations.filter((c) => {
            const noStructure =
                (c.regions?.length ?? 0) === 0 && (c.states?.length ?? 0) === 0;
            if (noStructure) return true;
            return (c.regions?.length ?? 0) > 0 || (c.states?.length ?? 0) > 0;
        });

        return merged.locations.length > 0 ? merged : null;
    }

    getCountriesBySeasonAndYear(
        seasonId: any,
        year: number,
        cropIds?: any[],
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CropMapLocationCountryResponse[] {
        const yl = this.getYearLocationsForSeasonAndYear(seasonId, year, cropIds, type);
        return yl ? this.getCountriesByYear([yl]) : [];
    }

    getCitiesByCountryIdForYear(
        countryId: any,
        seasonId: any,
        year: number,
        cropIds?: any[],
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CityModel[] {
        const yl = this.getYearLocationsForSeasonAndYear(seasonId, year, cropIds, type);
        if (!yl) return [];
        const country = this.getCountriesByYear([yl]).find((c) => c.countryId === countryId);
        if (!country) return [];
        const cities: CityModel[] = [];
        for (const region of country.regions ?? []) {
            for (const city of region.cities ?? []) {
                if (!cities.some((c) => c.cityId === city.cityId)) {
                    const cm = new CityModel();
                    cm.countryId = countryId;
                    cm.regionId = region.regionId;
                    cm.cityId = city.cityId;
                    cm.name = city.name;
                    cm.districts = city.districts;
                    cities.push(cm);
                }
            }
        }
        for (const state of country.states ?? []) {
            for (const city of state.cities ?? []) {
                if (!cities.some((c) => c.cityId === city.cityId)) {
                    const cm = new CityModel();
                    cm.countryId = countryId;
                    cm.stateId = state.stateId;
                    cm.cityId = city.cityId;
                    cm.name = city.name;
                    cm.districts = city.districts;
                    cities.push(cm);
                }
            }
        }
        return cities;
    }

    getSeasonsByCountryId(
        countryId: any,
        type: 'enterprise' | 'authorized' = 'authorized',
    ): CropMapSeasonViewModel[] {
        const seasons: CropMapSeasonViewModel[] = [];
        const cropYearRows =
            type == 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        for (const cropYearRow of cropYearRows) {
            for (const yearLocation of cropYearRow.yearLocations) {
                const countryPresent =
                    type === 'enterprise'
                        ? (
                              yearLocation as EnterpriseCropYearLocation['yearLocations'][number]
                          ).countries?.some((country) => country.id == countryId)
                        : (yearLocation as RoleYearLocation).locations?.some(
                              (hierarchy) => hierarchy.countryId == countryId,
                          );
                if (
                    countryPresent &&
                    !seasons.some((season) => this.idEq(season.id, cropYearRow.seasonId))
                ) {
                    seasons.push(
                        CropMapSeasonViewModel.fromJS({
                            id: cropYearRow.seasonId,
                            name: cropYearRow.seasonName,
                        }),
                    );
                }
            }
        }
        return seasons;
    }

    getYearsByCountryId(countryId: any, type: 'enterprise' | 'authorized' = 'authorized'): MapYearLocation[] {
        const years: MapYearLocation[] = [];
        const cropYearRows =
            type == 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        for (const cropYearRow of cropYearRows) {
            for (const yearLocation of cropYearRow.yearLocations) {
                const countryPresent =
                    type === 'enterprise'
                        ? (
                              yearLocation as EnterpriseCropYearLocation['yearLocations'][number]
                          ).countries?.some((country) => country.id == countryId)
                        : (yearLocation as RoleYearLocation).locations?.some(
                              (hierarchy) => hierarchy.countryId == countryId,
                          );
                if (!countryPresent) continue;
                const mapCountriesSlice =
                    type === 'enterprise'
                        ? this.enterpriseRefsToFlatCountries(
                              (
                                  yearLocation as EnterpriseCropYearLocation['yearLocations'][number]
                              ).countries?.filter((country) => country.id == countryId) ?? [],
                          )
                        : this.hierarchyToCropMapCountries(
                              (yearLocation as RoleYearLocation).locations?.filter(
                                  (hierarchy) => hierarchy.countryId == countryId,
                              ) ?? [],
                          );
                const existingYear = years.find((y) => y.year == yearLocation.year);
                if (!existingYear) {
                    years.push({ year: yearLocation.year, locations: mapCountriesSlice });
                } else {
                    this.mergeCountriesIntoBucket(existingYear.locations, mapCountriesSlice);
                }
            }
        }
        return years.sort((yearA, yearB) => yearA.year - yearB.year);
    }

    getCropsByCountryId(countryId: any, type: 'enterprise' | 'authorized' = 'authorized'): CropMapCropViewModel[] {
        const seenCropIds = new Set<string>();
        const crops: CropMapCropViewModel[] = [];
        const cropYearRows =
            type == 'enterprise' ? this.enterpriseCropYearLocations : this.authorizedCropYearLocations;
        for (const cropYearRow of cropYearRows) {
            for (const yearLocation of cropYearRow.yearLocations) {
                const countryPresent =
                    type === 'enterprise'
                        ? (
                              yearLocation as EnterpriseCropYearLocation['yearLocations'][number]
                          ).countries?.some((country) => country.id == countryId)
                        : (yearLocation as RoleYearLocation).locations?.some(
                              (hierarchy) => hierarchy.countryId == countryId,
                          );
                if (countryPresent && !seenCropIds.has(String(cropYearRow.cropId))) {
                    seenCropIds.add(String(cropYearRow.cropId));
                    crops.push(
                        CropMapCropViewModel.fromJS({
                            id: cropYearRow.cropId,
                            name: cropYearRow.cropName,
                            color: cropYearRow.cropColor,
                            seasonId: cropYearRow.seasonId,
                        }),
                    );
                }
            }
        }
        return crops;
    }



    // ═════════════════════════════════════════════════════════════════════
    // Enterprise ↔ authorized comparison
    // ═════════════════════════════════════════════════════════════════════

    compareEnterpriseAndAuthorizedLocations(): void {
        this.authorizedCropYearLocations = this.authorizedCropYearLocations.filter((authorizedRow) => {
            const enterpriseRow = this.enterpriseCropYearLocations.find(
                (row) => row.cropId == authorizedRow.cropId,
            );
            if (!enterpriseRow) return false;

            authorizedRow.yearLocations = authorizedRow.yearLocations.filter((authorizedYear) => {
                const enterpriseYear = enterpriseRow.yearLocations.find(
                    (yearLoc) => yearLoc.year == authorizedYear.year,
                );
                if (!enterpriseYear) return false;

                const allowedCountryIds = new Set(enterpriseYear.countries.map((country) => country.id));
                if (allowedCountryIds.size === 0) {
                    return true;
                }

                authorizedYear.locations = authorizedYear.locations.filter((hierarchy) =>
                    allowedCountryIds.has(hierarchy.countryId),
                );
                return authorizedYear.locations.length > 0;
            });

            return authorizedRow.yearLocations.length > 0;
        });
    }

    // ═════════════════════════════════════════════════════════════════════
    // Reset & clear
    // ═════════════════════════════════════════════════════════════════════

    get timeStamp(): number {
        return new Date().getTime();
    }

    resetAll(): void {
        this.enterpriseCropYearLocations = [];
        this.authorizedCropYearLocations = [];

        this.mapboxLayerConfigs = [];
        this.noCropMapAccess.set(false);
        this.fieldObservationEnterpriseEnabled.set(false);
        this.invalidateAccessLoadCache();
    }

    get access(): AccessEnterpriseViewModel | null {
        return this.accessSubject.value;
    }

    clearAccess(): void {
        this.accessSubject.next(null);
        this.localStorage.removeItem(CropMapAccessService.STORAGE_KEY);
    }

    private setAccess(value: AccessEnterpriseViewModel | null): void {
        this.accessSubject.next(value);
        if (value) {
            this.localStorage.setItem(CropMapAccessService.STORAGE_KEY, value);
        } else {
            this.localStorage.removeItem(CropMapAccessService.STORAGE_KEY);
        }
    }

    private loadFromStorage(): AccessEnterpriseViewModel | null {
        return this.localStorage.getItem<AccessEnterpriseViewModel>(
            CropMapAccessService.STORAGE_KEY,
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // Lookup enrichment – flat enterprise countries → full hierarchy
    // ═════════════════════════════════════════════════════════════════════

    enrichCountriesWithHierarchy(
        flatCountries: { id: string; name: string }[],
    ): Observable<CropMapLocationCountryResponse[]> {
        if (!flatCountries.length) return of([]);

        const calls = flatCountries.map((c) =>
            this.lookupProxy.lookup_GetCountryAddressHierarchy(c.id).pipe(
                map((res) => this.addressHierarchyToCountryResponse(c.id, c.name, res.result)),
                catchError(() => {
                    const fallback = new CropMapLocationCountryResponse();
                    (fallback as any).countryId = c.id;
                    fallback.name = c.name;
                    return of(fallback);
                }),
            ),
        );

        return forkJoin(calls);
    }

    private addressHierarchyToCountryResponse(
        countryId: string,
        countryName: string,
        hierarchy: CountryAddressHierarchyViewModel,
    ): CropMapLocationCountryResponse {
        const country = new CropMapLocationCountryResponse();
        (country as any).countryId = countryId;
        country.name = countryName;

        country.states = (hierarchy.states ?? []).map((node) => {
            const state = new CropMapLocationStateResponse();
            (state as any).stateId = node.key;
            state.name = node.value;
            state.cities = this.mapCityDistrictNodes(node.cities);
            return state;
        });

        country.regions = (hierarchy.regions ?? []).map((node) => {
            const region = new CropMapLocationRegionResponse();
            (region as any).regionId = node.key;
            region.name = node.value;
            region.cities = this.mapCityDistrictNodes(node.cities);
            return region;
        });

        return country;
    }

    private mapCityDistrictNodes(
        cities: { key: string; value: string | undefined; districts: { key: string; value: string | undefined }[] | undefined }[] | undefined,
    ): CropMapLocationCityResponse[] {
        return (cities ?? []).map((c) => {
            const city = new CropMapLocationCityResponse();
            (city as any).cityId = c.key;
            city.name = c.value;
            city.districts = (c.districts ?? []).map((d) => {
                const district = new CropMapLocationDistrictResponse();
                (district as any).districtId = d.key;
                district.name = d.value;
                return district;
            });
            return city;
        });
    }

    /**
     * Human-readable city/district names for a geographic unit from authorized role hierarchy.
     * `unitAccessKey` matches `unit_id` on field features (same as cityId / districtId access keys).
     */
    resolveUnitLocationLabels(
        unitType: string | undefined,
        unitAccessKey: string | undefined,
    ): { cityName: string; districtName: string } {
        const empty = { cityName: '', districtName: '' };
        const key = unitAccessKey?.trim();
        if (!key) {
            return empty;
        }

        const type = unitType?.trim().toLowerCase() ?? '';

        for (const cropRow of this.authorizedCropYearLocations ?? []) {
            for (const yearLocation of cropRow.yearLocations ?? []) {
                const countries = this.hierarchyToCropMapCountries(yearLocation.locations);
                for (const country of countries) {
                    if (type === 'country' && String(country.countryId) === key) {
                        return { cityName: country.name ?? '', districtName: '' };
                    }

                    for (const city of this.getCitiesByCountry(country)) {
                        if (type === 'city' && String(city.cityId) === key) {
                            return { cityName: city.name ?? '', districtName: '' };
                        }

                        for (const district of city.districts ?? []) {
                            if (type === 'district' && String(district.districtId) === key) {
                                return {
                                    districtName: district.name ?? '',
                                    cityName: city.name ?? '',
                                };
                            }
                        }
                    }
                }
            }
        }

        return empty;
    }

    clear(): void {
        this.resetAll();
        this.clearAccess();
    }
}
